/**
 * ShapesPanel - Right sidebar with shapes list and field assignment
 *
 * Compact panel with inline laser operation controls for graphics.
 * Uses icon-based dropdowns for Score/Engrave/Cut with color selection.
 */

import { useState, useRef, useEffect } from 'react';
import { Layers, Square, Image, ImageIcon, Trash2, PencilLine, Slice, Copy, Type } from 'lucide-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTemplateStore } from './useTemplateStore';
import type { TemplateShape, CustomizationField } from '@jigflow/shared';
import { XTOOL_LAYER_COLORS } from '@jigflow/shared/generators/xcsGenerator';
import { LIGHTBURN_LAYER_COLORS } from '@jigflow/shared/generators/lbrnGenerator';

// Paint bucket icon (not in lucide, using custom SVG)
const PaintBucketIcon = ({ size = 14, className = '' }: { size?: number; className?: string }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z"/>
        <path d="m5 2 5 5"/>
        <path d="M2 13h15"/>
        <path d="M22 20a2 2 0 1 1-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4Z"/>
    </svg>
);

interface ShapesPanelProps {
    fields: CustomizationField[];
    workAreaWidth: number;
    workAreaHeight: number;
    software?: string; // Machine software type (xtool, lightburn, generic)
    onFieldAssign: (shapeId: string, fieldId: string | null) => void;
    onDeleteShape: (shapeId: string) => void;
    onUpdateShape: (shapeId: string, updates: Partial<TemplateShape>) => void;
    onDuplicateShape?: (shapeId: string) => void; // Duplicate shape with same properties
    shapesWithSettings?: Set<string>; // Shape IDs that have custom process settings
    outputEnabledMap?: Map<string, Map<string, boolean>>; // shapeId -> operation -> outputEnabled
}

// Color dropdown component for laser operations
interface LaserOpDropdownProps {
    type: 'score' | 'engrave' | 'cut';
    enabled: boolean;
    color: string;
    outputEnabled?: boolean; // Whether output is enabled in process settings (true = will process, false = skipped)
    colors: readonly { value: string; label: string }[]; // Color palette to use
    onToggle: (enabled: boolean) => void;
    onColorChange: (color: string) => void;
    // Combined callback to avoid race condition when selecting a color
    onSelectColor?: (enabled: boolean, color: string) => void;
}

function LaserOpDropdown({ type, enabled, color, outputEnabled = true, colors, onToggle, onColorChange, onSelectColor }: LaserOpDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const Icon = type === 'score' ? PencilLine : type === 'engrave' ? PaintBucketIcon : Slice;
    const label = type === 'score' ? 'Score (outline)' : type === 'engrave' ? 'Engrave (fill)' : 'Cut (through)';

    // Determine visual state: enabled but output disabled = outline style
    const isOutputDisabled = enabled && !outputEnabled;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                className={`w-6 h-6 rounded flex items-center justify-center transition-all ${
                    enabled
                        ? isOutputDisabled
                            ? 'ring-1 ring-offset-1 ring-dashed ring-slate-400 dark:ring-slate-500 ring-offset-white dark:ring-offset-slate-800 opacity-60'
                            : 'ring-1 ring-offset-1 ring-slate-400 dark:ring-slate-500 ring-offset-white dark:ring-offset-slate-800'
                        : 'opacity-40 hover:opacity-70'
                }`}
                style={{
                    backgroundColor: enabled ? (isOutputDisabled ? 'transparent' : color) : '#64748b',
                    border: isOutputDisabled ? `2px dashed ${color}` : 'none'
                }}
                title={enabled ? (isOutputDisabled ? `${label}: Output OFF` : `${label}: ${colors.find(c => c.value === color)?.label || 'Custom'}`) : `${label}: Off`}
            >
                <Icon size={12} className={isOutputDisabled ? 'drop-shadow-sm' : 'text-white drop-shadow-sm'} style={{ color: isOutputDisabled ? color : undefined }} />
            </button>

            {isOpen && (
                <div className="absolute right-0 top-8 z-50 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-600 p-1.5 min-w-[100px]">
                    {/* Off option */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle(false);
                            setIsOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] transition ${
                            !enabled
                                ? 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-500 dark:text-slate-400'
                        }`}
                    >
                        <span className="w-4 h-4 rounded border-2 border-dashed border-slate-300 dark:border-slate-500" />
                        <span>Off</span>
                    </button>

                    <div className="h-px bg-slate-200 dark:bg-slate-600 my-1" />

                    {/* Color options */}
                    {colors.map(c => (
                        <button
                            key={c.value}
                            onClick={(e) => {
                                e.stopPropagation();
                                // Use combined callback to avoid race condition
                                if (onSelectColor) {
                                    onSelectColor(true, c.value);
                                } else {
                                    onToggle(true);
                                    onColorChange(c.value);
                                }
                                setIsOpen(false);
                            }}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[11px] transition ${
                                enabled && color === c.value
                                    ? 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200'
                                    : 'hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-500 dark:text-slate-400'
                            }`}
                        >
                            <span
                                className="w-4 h-4 rounded border border-slate-300 dark:border-slate-500"
                                style={{ backgroundColor: c.value }}
                            />
                            <span>{c.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// Sortable wrapper for shape items
interface SortableShapeItemProps {
    id: string;
    children: React.ReactNode;
}

function SortableShapeItem({ id, children }: SortableShapeItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 50 : 'auto',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
        >
            {children}
        </div>
    );
}

export function ShapesPanel({
    fields,
    workAreaWidth,
    workAreaHeight,
    software,
    onFieldAssign,
    onDeleteShape,
    onUpdateShape,
    onDuplicateShape,
    shapesWithSettings: _shapesWithSettings = new Set(),
    outputEnabledMap = new Map()
}: ShapesPanelProps) {
    void _shapesWithSettings; // Reserved for future use

    // Get software format label
    const getSoftwareLabel = () => {
        switch (software) {
            case 'xtool': return 'XCS';
            case 'lightburn': return 'LBRN';
            default: return 'SVG';
        }
    };
    const { shapes, selectedShapeId, selectShape, reorderShapes } = useTemplateStore();

    // Search state
    const [searchQuery, setSearchQuery] = useState('');

    // Configure sensors with 8px activation distance to distinguish click from drag
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Helper to get output enabled state for a shape+operation
    // Returns true (output ON) by default for operations without settings
    const getOutputEnabled = (shapeId: string, operation: 'score' | 'engrave' | 'cut'): boolean => {
        const shapeSettings = outputEnabledMap.get(shapeId);
        if (!shapeSettings) return true; // No settings for this shape = default ON
        const value = shapeSettings.get(operation);
        if (value === undefined) return true; // No setting for this operation = default ON
        return value;
    };

    // Select color palette based on software type
    const colorPalette = software === 'lightburn' ? LIGHTBURN_LAYER_COLORS : XTOOL_LAYER_COLORS;

    // LightBurn doesn't have a separate score operation (score = cut)
    const showScoreOperation = software !== 'lightburn';

    const getShapeIcon = (shape: TemplateShape) => {
        if (shape.type === 'raster') {
            // Amber icon for raster images (PNG, JPG, BMP)
            return <ImageIcon size={12} className="text-amber-500" />;
        }
        if (shape.type === 'graphic') {
            // Check if this is a TEXT shape from XCS import
            if (shape.originalXcsData?.type === 'TEXT') {
                return <Type size={12} className="text-blue-400" />;
            }
            return <Image size={12} className="text-purple-400" />;
        }
        return <Square size={12} className="text-slate-400" />;
    };

    const getShapeLabel = (shape: TemplateShape, index: number) => {
        // Check type first - rasters, graphics can be TEXT or regular graphics
        if (shape.type === 'raster') {
            const rasterIndex = shapes.filter((s, i) => s.type === 'raster' && i <= index).length;
            return `Raster ${rasterIndex}`;
        }
        if (shape.type === 'graphic') {
            // Check if this is a TEXT shape from XCS import
            const isText = shape.originalXcsData?.type === 'TEXT';
            if (isText) {
                const textIndex = shapes.filter((s, i) =>
                    s.type === 'graphic' && s.originalXcsData?.type === 'TEXT' && i <= index
                ).length;
                return `Text ${textIndex}`;
            }
            const graphicIndex = shapes.filter((s, i) =>
                s.type === 'graphic' && s.originalXcsData?.type !== 'TEXT' && i <= index
            ).length;
            return `Graphic ${graphicIndex}`;
        }

        // Check if it's a full-size border rect
        const isBorder = shape.type === 'rect' &&
            Math.abs(shape.width - workAreaWidth) < 1 &&
            Math.abs(shape.height - workAreaHeight) < 1 &&
            Math.abs(shape.x) < 1 && Math.abs(shape.y) < 1;

        if (isBorder) return 'Border';

        // Get field label if assigned
        if (shape.fieldId) {
            const field = fields.find(f => f.id === shape.fieldId);
            if (field) return field.label;
        }

        const rectIndex = shapes.filter((s, i) => s.type === 'rect' && i <= index).length;
        return `Custom ${rectIndex}`;
    };

    // Sort shapes for display: border first, then rasters, then graphics, then rectangles
    const sortedShapes = [...shapes].sort((a, b) => {
        const aIsFullSize = Math.abs(a.width - workAreaWidth) < 1 && Math.abs(a.height - workAreaHeight) < 1;
        const bIsFullSize = Math.abs(b.width - workAreaWidth) < 1 && Math.abs(b.height - workAreaHeight) < 1;

        if (aIsFullSize && !bIsFullSize) return -1;
        if (!aIsFullSize && bIsFullSize) return 1;

        // Rasters and graphics come before rects
        const aIsRasterOrGraphic = a.type === 'raster' || a.type === 'graphic';
        const bIsRasterOrGraphic = b.type === 'raster' || b.type === 'graphic';
        if (aIsRasterOrGraphic && !bIsRasterOrGraphic) return -1;
        if (!aIsRasterOrGraphic && bIsRasterOrGraphic) return 1;

        return a.orderIndex - b.orderIndex;
    });

    // Filter shapes by search query
    const filteredShapes = searchQuery
        ? sortedShapes.filter(shape => {
            const label = getShapeLabel(shape, shapes.indexOf(shape)).toLowerCase();
            return label.includes(searchQuery.toLowerCase());
        })
        : sortedShapes;

    // Handle drag end - reorder shapes
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        // Find indices in original shapes array
        const oldIndex = shapes.findIndex(s => s.id === active.id);
        const newIndex = shapes.findIndex(s => s.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            reorderShapes(oldIndex, newIndex);
        }
    };

    return (
        <div className="w-48 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col overflow-hidden">
            {/* Workarea info - fixed at top */}
            <div className="px-2 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex-shrink-0">
                <div className="text-[10px] text-slate-500 dark:text-slate-400">
                    <span className="font-medium">Workarea:</span> {workAreaWidth} × {workAreaHeight} mm
                </div>
                <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                    <span className="font-medium">Format:</span> {getSoftwareLabel()}
                </div>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
                {/* Shapes List */}
                <div className="p-2">
                    <h4 className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                        <Layers size={10} />
                        Shapes ({shapes.length})
                    </h4>
                    {/* Search input */}
                    <input
                        type="text"
                        placeholder="Search shapes..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-2 py-1 mb-1.5 text-[11px] rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={filteredShapes.map(s => s.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className="space-y-1">
                                {filteredShapes.map((shape) => {
                                    const isSelected = selectedShapeId === shape.id;
                                    const isAssigned = !!shape.fieldId;
                                    const isGraphic = shape.type === 'graphic';
                                    const isRaster = shape.type === 'raster';
                                    // Show laser controls for rasters, graphics, OR rect shapes with assigned fields
                                    const showLaserControls = isRaster || isGraphic || (shape.type === 'rect' && isAssigned);
                                    // Rasters only support engrave (no score/cut)
                                    const showAllOps = !isRaster;

                                    return (
                                        <SortableShapeItem key={shape.id} id={shape.id}>
                                            <div
                                                className={`rounded transition ${isSelected
                                                    ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-300 dark:ring-blue-700'
                                                    : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'
                                                }`}
                                            >
                                                {/* Main row - clickable */}
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => selectShape(shape.id)}
                                                    onKeyDown={(e) => e.key === 'Enter' && selectShape(shape.id)}
                                                    className="w-full flex items-center gap-1.5 px-1.5 py-1 text-left cursor-pointer"
                                                >
                                                    <span className={isAssigned ? 'text-green-500' : ''}>
                                                        {getShapeIcon(shape)}
                                                    </span>
                                                    <span className={`flex-1 min-w-0 truncate text-[11px] ${
                                                        isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-slate-600 dark:text-slate-300'
                                                    }`}>
                                                        {getShapeLabel(shape, shapes.indexOf(shape))}
                                                    </span>

                                                    {/* Inline laser operation controls */}
                                                    {showLaserControls && (
                                                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
                                                            {/* Score - not shown for rasters or LightBurn */}
                                                            {showAllOps && showScoreOperation && (
                                                                <LaserOpDropdown
                                                                    type="score"
                                                                    enabled={shape.score ?? false}
                                                                    color={shape.scoreColor || '#fe0002'}
                                                                    colors={colorPalette}
                                                                    outputEnabled={getOutputEnabled(shape.id, 'score')}
                                                                    onToggle={(enabled) => onUpdateShape(shape.id, { score: enabled })}
                                                                    onColorChange={(color) => onUpdateShape(shape.id, { scoreColor: color })}
                                                                    onSelectColor={(enabled, color) => onUpdateShape(shape.id, { score: enabled, scoreColor: color })}
                                                                />
                                                            )}
                                                            {/* Engrave - shown for all shapes including rasters */}
                                                            <LaserOpDropdown
                                                                type="engrave"
                                                                enabled={shape.engrave !== false}
                                                                color={shape.engraveColor || '#000000'}
                                                                colors={colorPalette}
                                                                outputEnabled={getOutputEnabled(shape.id, 'engrave')}
                                                                onToggle={(enabled) => onUpdateShape(shape.id, { engrave: enabled })}
                                                                onColorChange={(color) => onUpdateShape(shape.id, { engraveColor: color })}
                                                                onSelectColor={(enabled, color) => onUpdateShape(shape.id, { engrave: enabled, engraveColor: color })}
                                                            />
                                                            {/* Cut - not shown for rasters */}
                                                            {showAllOps && (
                                                                <LaserOpDropdown
                                                                    type="cut"
                                                                    enabled={shape.cut ?? false}
                                                                    color={shape.cutColor || '#00befe'}
                                                                    colors={colorPalette}
                                                                    outputEnabled={getOutputEnabled(shape.id, 'cut')}
                                                                    onToggle={(enabled) => onUpdateShape(shape.id, { cut: enabled })}
                                                                    onColorChange={(color) => onUpdateShape(shape.id, { cutColor: color })}
                                                                    onSelectColor={(enabled, color) => onUpdateShape(shape.id, { cut: enabled, cutColor: color })}
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Field assignment for selected rect shapes */}
                                                {isSelected && shape.type === 'rect' && (
                                                    <div className="px-1.5 py-1" onPointerDown={e => e.stopPropagation()}>
                                                        <select
                                                            value={shape.fieldId || ''}
                                                            onChange={(e) => onFieldAssign(shape.id, e.target.value || null)}
                                                            className="w-full px-1.5 py-0.5 text-[10px] bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded text-slate-800 dark:text-white"
                                                        >
                                                            <option value="">— Assign Field —</option>
                                                            {fields.filter(f => f.label !== '__variation_selector__').map(field => (
                                                                <option key={field.id} value={field.id}>{field.label}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )}

                                                {/* Action buttons - shown when selected */}
                                                {isSelected && (
                                                    <div className="px-1.5 pb-1.5 flex gap-1" onPointerDown={e => e.stopPropagation()}>
                                                        {onDuplicateShape && (
                                                            <button
                                                                onClick={() => onDuplicateShape(shape.id)}
                                                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-400 text-[10px] font-medium rounded transition"
                                                                title="Duplicate shape"
                                                            >
                                                                <Copy size={10} />
                                                                Duplicate
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => onDeleteShape(shape.id)}
                                                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 text-[10px] font-medium rounded transition"
                                                        >
                                                            <Trash2 size={10} />
                                                            Delete
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </SortableShapeItem>
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </DndContext>
                    {shapes.length === 0 && (
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 italic py-2 text-center">
                            No shapes
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
